/* eslint-disable no-spaced-func */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { PaginationMetaLink } from '../components/Modals/Paginator';
import { PaginationMeta } from '../models/Paginator';
import { BasicFileInterface } from './FileContext';
import api from '../services/api';
import FileManagerUtils from '../utils/FileManagerUtils';
import { VideoTypeValue } from '../utils/video';
import ModalComponent from '../components/ModalComponent';

export interface VideoInterface extends BasicFileInterface {
  src: string,
  type: VideoTypeValue,
}

interface VideoContextProps {
  total: number,
  currentPage?: number,
  links?: PaginationMetaLink[],
  paginationPageInfo?: PaginationMeta,
  videos?: VideoInterface[]
  fetchVideos?: (type?: VideoTypeValue, page?: number) => void
  deleteVideo?: (videoId: number, type: VideoTypeValue, cb?: () => void) => void
  createVideo?: (file: File, type: VideoTypeValue, cb?: () => void, cbe?: () => void) => void
  editVideo?: (videoId: number, type: VideoTypeValue, videoFile: File, cb?: () => void) => void
  errors?: any,
  isLoading?: boolean,
  fromItem: number,
  toItem: number
}

export const VideoContext = createContext<VideoContextProps>({ total: 0, fromItem: 0, toItem: 0 });

function VideosProvider({ children }: { children: React.ReactNode }) {
  const [total, setTotal] = useState<number>(0);
  const [links, setLinks] = useState(null);
  const [videos, setVideos] = useState<VideoInterface[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [toItem, setToItem] = useState(0);
  const [fromItem, setFromItem] = useState(0);

  const [paginationPageInfo, setPaginationPageInfo] = useState({
    current_page: 1,
    from: 1,
    last_page: 1,
    links: [],
    path: '',
    per_page: 1,
    to: 1,
    total: 1,
  });

  const [modalProperties, setModalProperties] = useState<{
    isOpen: boolean,
    fn: () => void
  }>({ isOpen: false, fn: () => { } });

  const fetchVideos = useCallback(async (type?: VideoTypeValue, page?: number) => {
    setIsLoading(true);
    page = page || paginationPageInfo.current_page;

    try {
      let searchTerm = `/v1/videos?page=${page}`;
      if (type) searchTerm += `&type=${type}`;

      const result = await api.get(searchTerm);

      setPaginationPageInfo(result.data.meta);
      setCurrentPage(result.data.meta.current_page);
      setTotal(result.data.meta.total);
      setLinks(result.data.meta.links);
      setVideos(result.data.data);
      if (result.data.meta.from) setFromItem(result.data.meta.from);
      if (result.data.meta.to) setToItem(result.data.meta.to);
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [paginationPageInfo.current_page]);

  const deleteVideo = useCallback(async (videoId: number, type?: VideoTypeValue, cb?: () => void) => {
    setIsLoading(true);

    try {
      await api.delete(`/v1/videos/${videoId}`)
        .then(() => {
          if (cb) cb();
          fetchVideos(type, currentPage);
        });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, fetchVideos]);

  const overwrite = useCallback((file: File, existingVideoFileId: number, cb: () => void) => {
    try {
      const formData = new FormData();
      formData.append('videoFile', file as any);

      api.post(`/v1/videos/edit/${existingVideoFileId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(() => {
        cb();
        setModalProperties({ ...modalProperties, isOpen: false });
        fetchVideos(VideoTypeValue.MY_VIDEO, currentPage);
      });
    } catch {
      console.error('Failed to overwrite file');
    }
  }, [currentPage, fetchVideos, modalProperties]);

  const createVideo = useCallback(async (
    videoFile: File,
    type?: VideoTypeValue,
    cb?: () => void,
    cbe?: () => void,
  ) => {
    if (!FileManagerUtils.checkFileExtension(videoFile, setErrors, true)) return;

    setIsLoading(true);

    FileManagerUtils.checkIfFileExists(
      '/v1/videos',
      videoFile.name,
      async () => {
        try {
          const formData = new FormData();

          formData.append('videoFile', videoFile as any);
          formData.append('type', type);

          await api.post('/v1/videos/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }).then(() => {
            if (cb) cb();
            fetchVideos(type, currentPage);
          });
        } catch (e: any) {
          const { data } = e.response;
          setErrors(data.errors);
          if (cbe) cbe();
          console.error(e);
        } finally {
          setIsLoading(false);
        }
      },
      setModalProperties,
      (videoFileId) => overwrite(videoFile, videoFileId, cb),
    );
  }, [fetchVideos, currentPage, overwrite]);

  const editVideo = useCallback(async (
    videoId: number,
    type: VideoTypeValue,
    videoFile: File,
    cb?: () => void,
  ) => {
    if (!FileManagerUtils.checkFileExtension(videoFile, setErrors, true)) return;

    setIsLoading(true);

    FileManagerUtils.checkIfFileExists(
      '/v1/videos',
      videoFile.name,
      async () => {
        try {
          const formData = new FormData();
          formData.append('videoFile', videoFile as any);

          await api.post(`/v1/videos/edit/${videoId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }).then(() => {
            if (cb) cb();
            fetchVideos(type, currentPage);
          });
        } catch (e) {
          console.error(e);
        } finally {
          setIsLoading(false);
        }
      },
      setModalProperties,
      () => overwrite(videoFile, videoId, cb),
    );
  }, [currentPage, fetchVideos, overwrite]);

  const exportValues = useMemo(() => ({
    total,
    currentPage,
    links,
    paginationMetaInfo: paginationPageInfo,
    videos,
    errors,
    fetchVideos,
    deleteVideo,
    createVideo,
    editVideo,
    isLoading,
    toItem,
    fromItem,
  }), [total, currentPage, links, paginationPageInfo, videos, errors, fetchVideos, deleteVideo, createVideo, editVideo, isLoading]);

  useEffect(() => {
    setModalProperties(modalProperties);
  }, [modalProperties]);

  return (
    <>
      <VideoContext.Provider value={exportValues}>
        {children}
      </VideoContext.Provider>

      <ModalComponent
        centered
        title="Confirm"
        show={modalProperties.isOpen}
        onConfirm={modalProperties.fn}
        confirmButtonText="Overwrite"
        closeModal={() => { setModalProperties({ ...modalProperties, isOpen: false }); setIsLoading(false); }}
        backdropClassName="z-index-3000"
        style={{ zIndex: 3000 }}
      >
        Overwrite the file. Are you sure?
      </ModalComponent>
    </>
  );
}

export default VideosProvider;
